import * as UseFeatureTracker from "@omni/services/tracking/trackers/feature/useFeature.tracker";
import { ReviewDistributionValue } from "@omni/types/api/reviews/bff/shared/reviewDistributionValue.model";
import { cn } from "@omni/utils/styles";

import { useReviewsContext } from "@/components/Product/Reviews/context/reviews.context";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { ConditionalWrapper } from "@/utils/helpers";

type StarFilterProps = {
    totalReviews: number;
    readonly?: boolean;
    distribution: Array<ReviewDistributionValue>;
};

export const StarFilter = ({
    totalReviews,
    distribution,
    readonly
}: StarFilterProps) => {
    const { sku, brand, category, ratings, toggleRating } = useReviewsContext();

    const isFiltered = (rating: string) => ratings.includes(rating);

    const createHandleOnSetRatingClick = (rating: string) => () => {
        if (!ratings.includes(rating)) {
            UseFeatureTracker.track({
                type: "bazaarvoice",
                name: "stars",
                filter: rating,
                sku: sku,
                brand: brand,
                categoryId: category
            });
        }

        toggleRating(rating);
    };

    const { t } = useGranite();

    return (
        <section>
            <p className={cn("tw:text-sm tw:mb-2")}>
                {t("product.reviews.summary.starFilter.selectRating")}
            </p>
            <div className={cn("tw:flex tw:flex-col tw:gap-1")}>
                {Array.from({ length: 5 }, (_, i) => (5 - i).toString()).map(
                    (rating) => {
                        const ratingValue =
                            distribution.find(({ key }) => key === rating)
                                ?.value ?? 0;

                        return (
                            <ConditionalWrapper
                                key={rating}
                                condition={!readonly}
                                wrapper={(children) => (
                                    <button
                                        aria-label={t(
                                            `general.reviews.filterByStar.${rating === "1" ? "single" : "multi"}`,
                                            rating
                                        )}
                                        className={cn(
                                            "tw:flex tw:items-center tw:p-0 tw:text-black tw:bg-transparent tw:border-none",
                                            "tw:hover:underline",
                                            "tw:hover:[&_progress[value]]:bg-grey-light tw:hover:[&_progress[value]::-webkit-progress-bar]:bg-grey-light tw:hover:[&_progress[value]::-webkit-progress-value]:bg-primary-focus tw:hover:[&_progress[value]::-moz-progress-bar]:bg-primary-focus"
                                        )}
                                        key={rating}
                                        onClick={createHandleOnSetRatingClick(
                                            rating
                                        )}
                                        type={"button"}
                                    >
                                        {children}
                                    </button>
                                )}
                                alternative={(children) => (
                                    <div
                                        className={cn(
                                            "tw:flex tw:items-center tw:p-0 tw:text-black tw:bg-transparent tw:border-none"
                                        )}
                                    >
                                        {children}
                                    </div>
                                )}
                            >
                                <span className={cn("tw:w-20 tw:text-left")}>
                                    {t(
                                        "product.reviews.summary.starFilter.stars",
                                        rating
                                    )}
                                </span>
                                <progress
                                    className={cn(
                                        "tw:h-[1.2rem] tw:rounded-xl tw:overflow-hidden tw:grow",
                                        "tw:[[value]]:bg-grey tw:[[value]]:border tw:[[value]]:border-grey-input",
                                        "tw:[&[value]::-webkit-progress-bar]:bg-grey tw:[&[value]::-webkit-progress-bar]:transition-colors",
                                        "tw:[&[value]::-webkit-progress-value]:bg-primary tw:[&[value]::-webkit-progress-value]:transition-colors",
                                        "tw:[&[value]::-moz-progress-bar]:bg-primary tw:[&[value]::-moz-progress-bar]:transition-colors",
                                        {
                                            "tw:[[value]]:bg-grey-light tw:[&[value]::-webkit-progress-bar]:bg-grey-light tw:[&[value]::-webkit-progress-value]:bg-primary-focus tw:[&[value]::-moz-progress-bar]:bg-primary-focus":
                                                isFiltered(rating)
                                        }
                                    )}
                                    max={totalReviews}
                                    value={ratingValue}
                                />
                                <span className={cn("tw:ml-4 tw:w-12")}>
                                    {ratingValue}
                                </span>
                            </ConditionalWrapper>
                        );
                    }
                )}
            </div>
        </section>
    );
};
